import type { BaseSchema } from 'yup';
import * as yup from 'yup';
import type { ILegoElem, ILegoElemLego } from './_Elem/models';
import { ERROR_MESSAGES } from '@src/consts/errors';
import { IEntityValidators } from '@src/models/entity';
import { AnyType } from '@src/models/common';

const buildYup = (validators: IEntityValidators): BaseSchema => {
  switch (validators.dataType) {
    case 'BOOLEAN':
      // Булен не может быть null или undefined.
      // Закладываем это на уровне платформы.
      // Если это надо, то стоит исопользовать другой управляющий элемент
      return yup.boolean().default(false);
    case 'NUMBER':
      const numValidator = yup
        .number()
        .typeError(ERROR_MESSAGES.NUMBER)
        .nullable(true)
        .transform((_, originValue) => {
          if (!originValue && originValue !== 0) return null;

          return +originValue.toString().replaceAll(',', '.');
        });

      if (validators.required) numValidator.nullable(false);

      // validators.integer - значение может только целочисленным
      if (validators.integer) {
        return numValidator.integer(ERROR_MESSAGES.NUMBER_INT);
      }

      // validators.decimal - значение может быть как целочисленным, так и дробным (максимально 2 цифры после запятой)
      if (validators.decimal) {
        return numValidator.test('is-decimal', ERROR_MESSAGES.NUMBER_DECIMAL, (value) => {
          if (value) return /^-?\d+(\.\d{0,2})?$/.test(value.toString());
          return true;
        });
      }
      return numValidator;
    case 'STRING':
      let ret = validators.required ? yup.string() : yup.string().nullable(true);
      // Пришлось написать кастомный валидатор, чтобы уйти от конфликта проверки на пустоту и минимальной длины
      if (validators.min) {
        ret = ret.test('min', ERROR_MESSAGES.STRING_MIN.replace('%s', validators.min.toString()), (value) => {
          if (!value) {
            return true;
          }
          return value.length >= Number(validators.min);
        });
      }
      if (validators.max) {
        ret = ret.max(validators.max, ERROR_MESSAGES.STRING_MAX.replace('%s', validators.max.toString()));
      }
      if (validators.email) {
        ret = ret.email(ERROR_MESSAGES.EMAIL);
      }
      if (validators.phone) {
        /** TODO: Добавить валидацию для разных форматов телефонов */
        ret = ret.test({
          test: (value) => {
            if (!value) return true;
            const clearedPhone = value.replace(/ |_+/g, '').trim();
            return clearedPhone.length >= 11;
          },
          message: ERROR_MESSAGES.PHONE,
        });
      }
      return ret;
    case 'STRUCT':
      return yup.mixed();
  }
};

const buildElemValidators = (elem: ILegoElem): BaseSchema | undefined => {
  if (elem.validators) {
    let validatorElem = buildYup(elem.validators);
    if (!validatorElem) {
      console.error('Скорее всего в валидаторе элемента на прописан dataType', elem);
    }

    if (elem.validators.required) {
      validatorElem = validatorElem.required(ERROR_MESSAGES.REQUIRED);
    }
    if (elem.validators.repeated) {
      // @link https://github.com/jquense/yup#array
      validatorElem = elem.validators.required
        ? yup.array().of(validatorElem).min(1, ERROR_MESSAGES.REQUIRED).required(ERROR_MESSAGES.REQUIRED)
        : yup.array().of(validatorElem).nullable(true);
    }

    if (Array.isArray(elem.validators.custom) && elem.validators.custom.length > 0) {
      for (const config of elem.validators.custom) {
        validatorElem = validatorElem.test(config);
      }
    }
    return validatorElem;
  }
  return undefined;
};

const fillElemsValidators = (schemaObj: { [key: string]: BaseSchema }, elems?: ILegoElem[]) => {
  if (elems) {
    elems.forEach((elem) => {
      const elemValidators = buildElemValidators(elem);
      if (elemValidators) {
        schemaObj[elem.id] = elemValidators;
      }

      const containerElem = elem as ILegoElemLego;

      if (
        containerElem?.lego?.elems !== undefined &&
        Array.isArray(containerElem?.lego.elems) &&
        containerElem?.lego.elems.length > 0
      ) {
        // пока контейнерные элементы не могут объединять свои субэлементы в суб-объект,
        // поэтому вылидировать можем так
        fillElemsValidators(schemaObj, containerElem.lego.elems);
      }
    });
  }
};

export const generateYupValidationSchema = (elems?: ILegoElem[]): AnyType => {
  const schemaObj = {} as { [key: string]: BaseSchema };
  fillElemsValidators(schemaObj, elems);
  return yup.object().shape(schemaObj);
};

/**
 * Актуально для сложных фарм, в которых сама форма меняется в зависимости от заполненных данных.
 * Например при создании тикета для конкретных категорий добавляются доп. элементы,
 * которые должны учавствовать в валидационных мероприятиях только, если они реально отображаются
 */
export const reBuildElems = (elems?: ILegoElem[], formData?: AnyType) => {
  const resultElems: ILegoElem[] = [];
  for (const elem of elems || []) {
    let newElem: ILegoElem | null = elem;
    if (elem.replaceCb) {
      newElem = elem.replaceCb(elem, formData);
    }

    if (newElem && newElem.existsCb) {
      // // Тут управляем динамическим рендерингом частей lego-формы.
      // // Типичная задача - при каких-то значениях каких-то частей lego формы рисовать доп. часть интерфейса.
      // // Если это пытаться делать на уровне передали в lego новых elems и реакции на изменение в onChange,
      // // мы попадает на нецдобный или вообще цикличный ререндеринг всего лего
      if (!newElem.existsCb(formData)) {
        newElem = null;
      }
    }

    if (newElem) {
      resultElems.push(newElem);
    }
  }
  return resultElems;
};
